import React, { createContext, useState, useEffect, useContext } from 'react';
import { Notificacao } from '../services/notificacaoService';
import notificacaoService from '../services/notificacaoService';

interface NotificacaoContextData {
    notificacoesNaoLidas: Notificacao[];
    contadorNaoLidas: number;
    carregarNotificacoes: () => Promise<void>;
    marcarComoLida: (notificacaoId: string) => Promise<void>;
    marcarTodasComoLidas: () => Promise<void>;
    adicionarNotificacao: (notificacao: Notificacao) => void;
}

const NotificacaoContext = createContext<NotificacaoContextData>({} as NotificacaoContextData);

export const NotificacaoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState<Notificacao[]>([]);
    const [contadorNaoLidas, setContadorNaoLidas] = useState<number>(0);
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

    // Carregar notificações iniciais
    const carregarNotificacoes = async () => {
        try {
            const naoLidas = await notificacaoService.buscarNotificacoesNaoLidas();
            setNotificacoesNaoLidas(naoLidas);
            setContadorNaoLidas(naoLidas.length);
        } catch (error) {
            console.error('Erro ao carregar notificações:', error);
        }
    };

    // Inicializar e configurar atualização periódica
    useEffect(() => {
        carregarNotificacoes();

        // Verificar novas notificações a cada 1 minuto
        const id = setInterval(() => {
            carregarNotificacoes();
        }, 60000);

        setIntervalId(id);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, []);

    // Marcar notificação como lida
    const marcarComoLida = async (notificacaoId: string) => {
        try {
            await notificacaoService.marcarComoLida(notificacaoId);

            // Atualizar o estado local
            setNotificacoesNaoLidas(prev =>
                prev.filter(n => n.guid !== notificacaoId)
            );

            setContadorNaoLidas(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Erro ao marcar notificação como lida:', error);
        }
    };

    // Marcar todas como lidas
    const marcarTodasComoLidas = async () => {
        try {
            await notificacaoService.marcarTodasComoLidas();
            setNotificacoesNaoLidas([]);
            setContadorNaoLidas(0);
        } catch (error) {
            console.error('Erro ao marcar todas notificações como lidas:', error);
        }
    };

    // Adicionar nova notificação (para testes ou quando receber via WebSocket)
    const adicionarNotificacao = (notificacao: Notificacao) => {
        if (!notificacao.lida) {
            setNotificacoesNaoLidas(prev => [notificacao, ...prev]);
            setContadorNaoLidas(prev => prev + 1);
        }
    };

    return (
        <NotificacaoContext.Provider
            value={{
                notificacoesNaoLidas,
                contadorNaoLidas,
                carregarNotificacoes,
                marcarComoLida,
                marcarTodasComoLidas,
                adicionarNotificacao
            }}
        >
            {children}
        </NotificacaoContext.Provider>
    );
};

export function useNotificacao() {
    const context = useContext(NotificacaoContext);

    if (!context) {
        throw new Error('useNotificacao deve ser usado dentro de NotificacaoProvider');
    }

    return context;
}

export default NotificacaoContext;
